import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import { Text, Group, Stack, Title } from '@mantine/core';
import { useComputedColorScheme } from '@mantine/core';
import { Competency, Grade, Swimlane, CardSize, UserCompetency, User } from '../../types';
import { useTags } from '../../hooks/useReferenceApi';
import { CompetencyDrawer } from './CompetencyDrawer';
import { CompetencyCreateModal } from './CompetencyCreateModal';
import { CompetencyMapModal } from './CompetencyMapModal';
import { SwimlaneEditModal } from './SwimlaneEditModal';
import { GradeEditModal } from './GradeEditModal';
import { CompetencyCard } from './CompetencyCard';

// Импортируем стили Swiper
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { usePermission } from '../../hooks/usePermission';

interface CompetencyMapMobileProps {
  data: {
    grades: Grade[];
    swimlanes: Swimlane[];
  };
  competencyMapId: string;
  currentGradeId?: string;
  nextGrade?: Grade;
  cardSize: CardSize;
  userCompetencies?: UserCompetency[];
  currentUser?: User;
  onCompetencyUpdate: (competency: Competency) => void;
  onCompetencyDelete: (competencyId: string) => void;
  onCompetencyCreate: (competency: {
    title: string;
    description: string;
    swimlaneId?: string;
    gradeId?: string;
    tags: string[];
    mentors: string[];
    isRequired?: boolean;
  }) => void;
  onSwimlaneUpdate: (swimlane: Swimlane) => void;
  onGradeUpdate: (grade: Grade) => void;
  /** Карта-пример: без правки компетенций даже у админа. */
  isExample?: boolean;
}

export const CompetencyMapMobile: React.FC<CompetencyMapMobileProps> = ({
  data,
  competencyMapId,
  currentGradeId,
  cardSize,
  nextGrade,
  userCompetencies = [],
  currentUser,
  onCompetencyUpdate,
  onCompetencyDelete,
  onCompetencyCreate,
  onSwimlaneUpdate,
  onGradeUpdate,
  isExample = false,
}) => {
  // Редактирование компетенции — только админ (бэкенд: requireAdmin).
  // Править карту — право maps.manage.
  const canManageMaps = usePermission('maps.manage');
  const isAdmin = canManageMaps && !isExample;
  const colorScheme = useComputedColorScheme('light');
  const isDark = colorScheme === 'dark';
  const { data: tagDict = [] } = useTags();

  const [selectedCompetency, setSelectedCompetency] = useState<Competency | null>(null);
  const [drawer, setDrawer] = useState<{
    comp: Competency;
    gradeIdx: number;
    swimlane: Swimlane;
  } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showSwimlaneModal, setShowSwimlaneModal] = useState(false);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [selectedSwimlane] = useState<Swimlane | null>(null);
  const [selectedGrade] = useState<Grade | null>(null);

  // Находим индекс текущего грейда
  const currentGradeIndex = data.grades.findIndex((grade) => grade._id === currentGradeId) || 0;

  const handleCompetencyClick = (competency: Competency, gradeIdx: number, swimlane: Swimlane) => {
    setDrawer({ comp: competency, gradeIdx, swimlane });
  };

  const handleEditCompetency = (competency: Competency) => {
    setSelectedCompetency(competency);
    setShowCreateModal(true);
  };

  // Обработчик для создания/обновления компетенции через CompetencyCreateModal
  const handleCompetencyModalSubmit = (competencyData: {
    title: string;
    description: string;
    swimlaneId?: string;
    gradeId?: string;
    tags: string[];
    mentors: string[];
    isRequired?: boolean;
  }) => {
    if (selectedCompetency) {
      // Обновление существующей компетенции
      onCompetencyUpdate({
        ...selectedCompetency,
        title: competencyData.title,
        description: competencyData.description,
        swimlaneId: competencyData.swimlaneId || selectedCompetency.swimlaneId,
        tags: (competencyData.tags || []).map((tg: string) => {
          const found = tagDict.find((td) => td._id === tg);
          return { _id: tg, name: found?.name ?? tg };
        }),
        mentors: competencyData.mentors.map((mentor) => {
          if (typeof mentor === 'string') {
            return {
              _id: mentor,
              name: '',
              email: '',
              position: '',
            };
          }
          return mentor;
        }),
      });
    } else {
      // Создание новой компетенции
      onCompetencyCreate(competencyData);
    }
    setShowCreateModal(false);
  };

  return (
    <>
      <Swiper
        modules={[Pagination, Navigation]}
        spaceBetween={20}
        slidesPerView={1.4}
        centeredSlides={true}
        autoHeight={true}
        initialSlide={currentGradeIndex}
        pagination={{ clickable: true }}
        navigation={false}
        className="competency-map-swiper"
        style={
          {
            padding: '20px 0',
            '--swiper-pagination-color': isDark
              ? 'var(--mantine-color-teal-4)'
              : 'var(--mantine-color-teal-6)',
          } as React.CSSProperties
        }
      >
        {data.grades.map((grade, gradeIdx) => (
          <SwiperSlide key={grade._id}>
            <div
              className={`grade-slide ${grade._id === currentGradeId ? 'grade-slide--current' : ''} ${grade._id === nextGrade?._id ? 'grade-slide--next' : ''}`}
            >
              <div className="grade-header">
                <Group justify="space-between" align="center" mb="md">
                  <div>
                    <Title
                      order={3}
                      className={grade._id === currentGradeId ? 'current-grade' : ''}
                    >
                      {grade.title}
                    </Title>
                    {grade.description && (
                      <Text size="sm" c="dimmed" mt={4}>
                        {grade.description}
                      </Text>
                    )}
                  </div>
                </Group>
              </div>

              <Stack gap="md">
                {data.swimlanes
                  .filter((swimlane) => {
                    const competencies =
                      grade.competencies?.filter((comp) => comp.swimlaneId === swimlane._id) || [];
                    return competencies.length > 0;
                  })
                  .map((swimlane) => {
                    const competencies =
                      grade.competencies?.filter((comp) => comp.swimlaneId === swimlane._id) || [];

                    return (
                      <div key={swimlane._id} className="swimlane-section">
                        <Group justify="space-between" align="center" mb="sm">
                          <Text fw={500} size="sm">
                            {swimlane.title}
                          </Text>
                        </Group>

                        <Stack gap="xs">
                          {competencies.map((competency) => {
                            const userCompetency = userCompetencies?.find(
                              (uc) => uc.competencyId === competency._id,
                            );
                            const userHas = !!userCompetency;

                            return (
                              <CompetencyCard
                                key={competency._id}
                                comp={competency}
                                gradeIdx={gradeIdx}
                                swimlane={swimlane}
                                onClick={() =>
                                  handleCompetencyClick(competency, gradeIdx, swimlane)
                                }
                                dragProps={{}}
                                dragHandleProps={{}}
                                isDragging={false}
                                isDragDropEnabled={false}
                                cardSize={cardSize}
                                userHas={userHas}
                                userCompetency={userCompetency}
                                isRequired={competency.isRequired}
                                onEdit={
                                  isAdmin ? () => handleEditCompetency(competency) : undefined
                                }
                                onRequestReview={() => {}}
                              />
                            );
                          })}
                        </Stack>
                      </div>
                    );
                  })}
              </Stack>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Модальные окна и drawer остаются без изменений */}
      {drawer && (
        <CompetencyDrawer
          drawer={drawer}
          onClose={() => setDrawer(null)}
          grades={data.grades.map((g) => g.title)}
          currentUser={currentUser}
          userCompetencies={userCompetencies}
          competencyMapId={competencyMapId}
        />
      )}

      <CompetencyCreateModal
        opened={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        swimlanes={data.swimlanes}
        grades={data.grades}
        editingCompetency={selectedCompetency}
        competencyMapId={competencyMapId}
        onSubmit={handleCompetencyModalSubmit}
        onRemoveFromMap={() => {
          setShowCreateModal(false);
          setSelectedCompetency(null);
          // Вызываем callback для удаления компетенции
          if (selectedCompetency) {
            onCompetencyDelete(selectedCompetency._id);
          }
        }}
      />

      <CompetencyMapModal
        opened={showMapModal}
        onClose={() => setShowMapModal(false)}
        editMap={null}
      />

      {selectedSwimlane && (
        <SwimlaneEditModal
          opened={showSwimlaneModal}
          onClose={() => setShowSwimlaneModal(false)}
          swimlane={selectedSwimlane}
          onSwimlaneUpdated={(swimlaneId, swimlaneData) => onSwimlaneUpdate(swimlaneData)}
        />
      )}

      {selectedGrade && (
        <GradeEditModal
          opened={showGradeModal}
          onClose={() => setShowGradeModal(false)}
          grade={selectedGrade}
          onGradeUpdated={(gradeId, gradeData) => onGradeUpdate(gradeData)}
        />
      )}
    </>
  );
};
